/* eslint-disable no-param-reassign */
import React, {
	forwardRef,
	useEffect,
	useRef,
	useState,
	memo,
} from 'react';
import Hls from 'hls.js';
import classNames from 'classnames';

type ProgressUpdate = {
	played: number;
	playedSeconds: number;
	loaded: number;
	loadedSeconds: number;
};

export type CrossOrigin = '' | 'anonymous' | 'use-credentials' | undefined;

type CustomMediaVideoProps = {
	id?: string;
	src: string;
	isHlsVideo: boolean;
	className?: string;
	duration?: number;
	elapsed: number;
	onEnded?: () => void;
	onError?: (error: Error) => void;
	onPlay?: () => void;
	onProgress?: (state: ProgressUpdate) => void;
	muted?: boolean;
	crossOrigin: CrossOrigin;
	loop?: boolean;
	posterImage?: string;
	autoPlay?: boolean;
};

// Add custom props to all the optional props a video element could have
type MediaVideoProps = CustomMediaVideoProps & Partial<React.DOMAttributes<HTMLVideoElement>>;

const MediaVideo = forwardRef(
	(
		{
			src,
			isHlsVideo,
			className,
			duration,
			elapsed,
			onEnded,
			onError,
			onPlay,
			onProgress,
			muted = false,
			crossOrigin,
			loop = false,
			posterImage,
			autoPlay = false,
			...otherProps
		}: MediaVideoProps,
		forwardVideoRef: any,
	): JSX.Element => {
		const hlsRef = useRef<Hls | null>(null);
		const timeUpdateRef = useRef<ProgressUpdate>({
			played: 0,
			playedSeconds: 0,
			loaded: 0,
			loadedSeconds: 0,
		});
		const [subsequentUnmount, setSubsequentUnmount] = useState(false);
		const [showPoster, setShowPoster] = useState(true);

		useEffect(() => {
			// Currently ivs creates an extra bit of duration for hls videos created
			// if the playSeconds is larger than the duration then we're at the end of the video.
			const oneFrame = 1 / 24;
			if (
				duration
				&& duration !== 0
				&& timeUpdateRef.current.playedSeconds > duration - oneFrame
				&& isHlsVideo
			) {
				if (loop) {
					forwardVideoRef.current.seekTo(0);
				} else {
					onEnded?.();
				}
			}
		}, [
			duration,
			forwardVideoRef,
			isHlsVideo,
			loop,
			onEnded,
			timeUpdateRef.current.playedSeconds,
		]);

		useEffect(() => {
			if (isHlsVideo && forwardVideoRef?.current && src) {
				hlsRef.current = new Hls();
				hlsRef.current.loadSource(src);
				hlsRef.current.attachMedia(forwardVideoRef.current);

				return () => {
					if (isHlsVideo && hlsRef.current?.destroy) {
						hlsRef.current.destroy();
					}
				};
			}
			return undefined;
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [src, isHlsVideo]);

		useEffect(() => () => {
			if (subsequentUnmount) {
				if (isHlsVideo && hlsRef.current?.destroy) {
					hlsRef.current.destroy();
				}
			}
			setSubsequentUnmount(true);
		}, [subsequentUnmount]);

		useEffect(() => {
			onProgress?.(timeUpdateRef.current);
		}, [onProgress]);

		useEffect(() => {
			if (showPoster && elapsed > 0) {
				setShowPoster(false);
			}
		}, [elapsed]);

		return (
			<div
				className={classNames('media-video-container', 'flex-center', {
					'ivs-player-container': isHlsVideo,
				})}
			>
				<video
					className={classNames(className, { 'ivs-player-video': isHlsVideo })}
					src={!isHlsVideo ? src : undefined}
					crossOrigin={crossOrigin}
					playsInline={true}
					data-testid='videoplayer'
					ref={forwardVideoRef}
					loop={loop}
					muted={muted}
					autoPlay={autoPlay}
					{...{
						onEnded,
						onError,
						onPlay,
						onProgress,
					}}
					/*
						Pass all of the other video props not listed directly
						above through to the video element.

						This includes `CustomMediaVideoProps` as well as all
						`HTMLVideoElement` props, so this can be treated like
						an extended `<video />` element.
					*/
					{...otherProps}
					onPlaying={() => {
						if (showPoster) {
							setShowPoster(false);
						}
					}}
				/>

				{posterImage && showPoster && (
					<div className='media-video-poster'>
						<img src={posterImage} alt={''} />
					</div>
				)}
			</div>
		);
	},
);

MediaVideo.displayName = 'MediaVideo';

export default memo(MediaVideo);
